// src/helpers/supabaseHelper.js
import supabase from './../supabaseClient' // Adjust the path as necessary

/**
 * Retrieves the elementClassification JSON data for a given propertyUUID.
 * @param {string} propertyUUID - The UUID of the property.
 * @returns {Promise<Object>} - The elementClassification JSON data.
 * @throws {Error} - Throws an error if the property is not found or if the query fails.
 */
export const getElementClassificationByPropertyUUID = async (propertyUUID) => {
  if (!propertyUUID) {
    throw new Error('A valid propertyUUID must be provided.')
  }

  try {
    const { data, error } = await supabase
      .from('property_scans') // Adjust table name as needed
      .select('element_classification')
      .eq('property_uuid', propertyUUID)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error(`No property found with propertyUUID: ${propertyUUID}`)
      } else {
        throw new Error(`Error retrieving data: ${error.message}`)
      }
    }

    if (!data || !data.element_classification) {
      throw new Error(`elementClassification not found for propertyUUID: ${propertyUUID}`)
    }

    return data.element_classification
  } catch (err) {
    console.error(`getElementClassificationByPropertyUUID Error: ${err.message}`)
    throw err
  }
}

/**
 * Updates the elementClassification JSON data for a given propertyUUID.
 * @param {string} propertyUUID - The UUID of the property.
 * @param {Object} updatedJson - The updated elementClassification JSON data.
 * @returns {Promise<Object>} - The updated property data.
 * @throws {Error} - Throws an error if the update fails.
 */
export const updateElementClassification = async (propertyUUID, updatedJson) => {
  if (!propertyUUID) {
    throw new Error('A valid propertyUUID must be provided.')
  }

  if (!updatedJson || typeof updatedJson !== 'object') {
    throw new Error('A valid updatedJson object must be provided.')
  }

  try {
    const { data, error } = await supabase
      .from('property_scans') // Adjust table name as needed
      .update({ element_classification: updatedJson })
      .eq('property_uuid', propertyUUID)
      .single()

    if (error) {
      throw new Error(`Error updating data: ${error.message}`)
    }

    return data
  } catch (err) {
    console.error(`updateElementClassification Error: ${err.message}`)
    throw err
  }
}
